import { type RefObject, useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { FeedbackOption, useGetChatMessageFeedbackOptionsQuery, usePostMessageFeedbackMutation } from '@/api/chat';
import {
  globalToastService,
  ThumbsDownIcon,
  ThumbsUpIcon,
  Tooltip,
  TOOLTIP_SURFACE_BOTTOM,
} from '@repo/atomic-components';
import { FeedbackModal } from '@/ui/components/FeedbackModal';
import type { FeedbackReminderPhase } from '../hooks/useFeedbackReminder';

interface MessageFeedbackButtonsProps {
  messageId: string;
  /** Enlarges, colours and pulses the buttons while the response asks for feedback. */
  reminderPhase?: FeedbackReminderPhase;
  /** Called when either button is pressed. */
  onFeedbackClick?: () => void;
}

const FALLBACK_OPTIONS: FeedbackOption[] = [
  { value: 'IRRELEVANT_CONTENT', label: 'Irrelevant Content' },
  { value: 'INCORRECT_INFORMATION', label: 'Incorrect Information' },
  { value: 'POOR_FORMATTING', label: 'Poor Formatting' },
  { value: 'RESPONSE_TOO_LONG', label: 'Response Too Long' },
  { value: 'RESPONSE_TOO_SHORT', label: 'Response Too Short' },
  { value: 'INCORRECT_OR_MISSING_CITATIONS', label: 'Incorrect Or Missing Citations' },
  { value: 'IMPROVE_PROMPT', label: 'Improve Prompt' },
  { value: 'OTHER', label: 'Other' },
];

const LIKE_RESPONSE_TOOLTIP = 'Good response';
const DISLIKE_RESPONSE_TOOLTIP = 'Poor response';

const getButtonClassName = (reminderPhase: FeedbackReminderPhase, accentClassName: string, hoverClassName: string) =>
  classNames(
    'flex cursor-pointer items-center justify-center border-none bg-transparent p-0',
    'transition-[width,height,color] motion-reduce:transition-none',
    {
      [`text-theme-text-grey ${hoverClassName} h-6 w-6 duration-150`]: reminderPhase === 'off',
      [`${accentClassName} h-12 w-12 duration-250 ease-[cubic-bezier(0.22,1,0.36,1)]`]: reminderPhase === 'on',
      'text-theme-text-grey h-6 w-6 duration-1000 ease-in-out': reminderPhase === 'leaving',
    },
  );

const getIconClassName = (reminderPhase: FeedbackReminderPhase) =>
  classNames('size-2/3', {
    'feedback-reminder-pulse': reminderPhase !== 'off',
    'is-leaving': reminderPhase === 'leaving',
  });

export const MessageFeedbackButtons = ({
  messageId,
  reminderPhase = 'off',
  onFeedbackClick,
}: MessageFeedbackButtonsProps) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [postFeedback, { isLoading: isSubmitting }] = usePostMessageFeedbackMutation();
  const { data: { reasons } = {} } = useGetChatMessageFeedbackOptionsQuery(undefined, { skip: !isModalVisible });
  const options = reasons?.length ? reasons : FALLBACK_OPTIONS;
  const thumbsUpTargetRef = useRef<HTMLButtonElement>(null);
  const thumbsDownTargetRef = useRef<HTMLButtonElement>(null);

  const handleThumbsUp = async () => {
    onFeedbackClick?.();
    try {
      await postFeedback({
        messageId,
        isPositive: true,
      }).unwrap();
      globalToastService.show({
        closable: true,
        severity: 'success',
        summary: 'Thank you for your feedback.',
        life: 6000,
      });
    } catch {
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary:
          "Something went wrong and your feedback wasn't saved. We really appreciate you taking the time - please try again later.",
        life: 6000,
      });
    }
  };

  const handleNegativeFeedbackSubmit = async (reason: string, comment: string) => {
    try {
      await postFeedback({
        messageId,
        isPositive: false,
        reason,
        comment,
      }).unwrap();

      globalToastService.show({
        closable: true,
        severity: 'success',
        summary: 'Thank you for your feedback.',
        life: 6000,
      });
    } catch {
      globalToastService.show({
        closable: true,
        severity: 'error',
        summary:
          "Something went wrong and your feedback wasn't saved. We really appreciate you taking the time - please try again later.",
        life: 6000,
      });
    }
  };

  return (
    <>
      <button
        ref={thumbsUpTargetRef}
        type="button"
        onClick={handleThumbsUp}
        aria-label={LIKE_RESPONSE_TOOLTIP}
        className={getButtonClassName(reminderPhase, 'text-theme-dark-green', 'hover:text-theme-dark-green')}
      >
        <ThumbsUpIcon strokeWidth={1.5} className={getIconClassName(reminderPhase)} />
      </button>
      <Tooltip
        target={thumbsUpTargetRef as RefObject<HTMLElement>}
        content={LIKE_RESPONSE_TOOLTIP}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
        baseZIndex={10000}
      />
      <button
        ref={thumbsDownTargetRef}
        type="button"
        onClick={() => {
          onFeedbackClick?.();
          setIsModalVisible(true);
        }}
        aria-label={DISLIKE_RESPONSE_TOOLTIP}
        className={getButtonClassName(reminderPhase, 'text-theme-text-red', 'hover:text-theme-text-red')}
      >
        <ThumbsDownIcon strokeWidth={1.5} className={getIconClassName(reminderPhase)} />
      </button>
      <Tooltip
        target={thumbsDownTargetRef as RefObject<HTMLElement>}
        content={DISLIKE_RESPONSE_TOOLTIP}
        position="bottom"
        className={TOOLTIP_SURFACE_BOTTOM}
        baseZIndex={10000}
      />

      <FeedbackModal
        options={options}
        isLoading={isSubmitting}
        onSubmit={handleNegativeFeedbackSubmit}
        visible={isModalVisible}
        onHide={() => setIsModalVisible(false)}
      />
    </>
  );
};
